import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from "react"
import { useAuth } from "@/lib/auth-context"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Plus, Pencil, Trash2 } from "lucide-react"
import { formatBRDate, todayISODate } from "@/lib/br-dates"
import { formatBRL as formatMoney, maskMoneyInput, splitInstallments, toMoneyNumber } from "@/lib/money"
import { PEDIDOS_PARCELAS_CONFIG } from "@/features/pagamentos/parcelas-logic"
import { useParcelas } from "@/features/pagamentos/use-parcelas"
import { API_BASE } from '@/lib/api-base'

interface PedidoItem {
  id: string
  produto: string
  quantidade: number
  unidade: string
  preco_unitario: number
  subtotal: number
  item_estoque_id?: string | null
}

interface ItemEstoqueVendavel {
  id: string
  nome: string
  unidade: string
  categoria: string
  preco_medio: number
  vendavel: boolean
}

type FormPedidoItem = {
  produto: string
  quantidade: number
  unidade: string
  preco_unitario: number
  item_estoque_id: string | null
  produtoManual: boolean
}

const UNIDADES_PRODUTO = ["DZ", "UN", "CX"] as const

function defaultFormItem(): FormPedidoItem {
  return {
    produto: "",
    quantidade: 0,
    unidade: "DZ",
    preco_unitario: 0,
    item_estoque_id: null,
    produtoManual: false,
  }
}

function normalizeUnidadeProduto(unidade: string): string {
  const upper = unidade.trim().toUpperCase()
  if (upper === "DZ" || upper === "UN" || upper === "CX") return upper
  if (upper.includes("DUZ")) return "DZ"
  if (upper.includes("CAIX")) return "CX"
  return "UN"
}

interface Pedido {
  id: string
  numero: string
  cliente_id: string | null
  cliente_nome: string | null
  data_pedido: string
  status: string
  valor_total: number
  observacao: string | null
  itens: PedidoItem[]
  criado_em: string
  criado_por_nome: string | null
}

interface Cliente {
  id: string
  nome: string
  status: string
}

const STATUS_LABELS: Record<string, string> = {
  RASCUNHO: "Rascunho",
  CONFIRMADO: "Confirmado",
  ENTREGUE: "Entregue",
  CANCELADO: "Cancelado",
}

const STATUS_COLORS: Record<string, "secondary" | "default" | "destructive" | "outline"> = {
  RASCUNHO: "secondary",
  CONFIRMADO: "default",
  ENTREGUE: "default",
  CANCELADO: "destructive",
}

const formatBRL = (v: number) => formatMoney(v)

const parseBRL = (s: string): number => toMoneyNumber(s)

const parseNonNegative = (raw: string): number => {
  const parsed = parseFloat(raw)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0
}

function PedidosPage() {
  const apiBase = API_BASE
  const { user, apiFetch, isSuperAdmin, selectedTenantId } = useAuth()
  const [pedidos, setPedidos] = useState<Pedido[]>([])
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [itensVendaveis, setItensVendaveis] = useState<ItemEstoqueVendavel[]>([])
  const [showForm, setShowForm] = useState(false)
  const [editPedido, setEditPedido] = useState<Pedido | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [filterStatus, setFilterStatus] = useState<string>("")
  const latestFilterStatusRef = useRef<string>("")
  const [form, setForm] = useState<{
    cliente_id: string
    data_pedido: string
    observacao: string
    itens: FormPedidoItem[]
  }>({
    cliente_id: "",
    data_pedido: todayISODate(),
    observacao: "",
    itens: [defaultFormItem()],
  })
  
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [pedidoToConfirm, setPedidoToConfirm] = useState<Pedido | null>(null)
  const [valorEntrada, setValorEntrada] = useState("")
  const [gerarConta, setGerarConta] = useState(true)

  const valorEntradaNum = parseBRL(valorEntrada)

  const {
    parcelas,
    novaParcela,
    setNovaParcela,
    resetParcelasForm,
    adicionarParcela,
    removerParcela,
    adicionarParcelaRapidaPorTipo,
    calcularDataParcela,
    saldoDevedor,
    restanteParaPagar,
  } = useParcelas({
    ...PEDIDOS_PARCELAS_CONFIG,
    getSaldoDevedor: () => (pedidoToConfirm?.valor_total || 0) - valorEntradaNum,
    getDataBaseFallback: () => pedidoToConfirm?.data_pedido || todayISODate(),
  })

  const fetchPedidos = useCallback(async () => {
    const requestedFilter = filterStatus
    latestFilterStatusRef.current = requestedFilter
    try {
      const url = requestedFilter
        ? `${apiBase}/api/pedidos/?status=${requestedFilter}`
        : `${apiBase}/api/pedidos/`
      const res = await apiFetch(url)
      if (res.ok) {
        const data = await res.json()
        if (latestFilterStatusRef.current === requestedFilter) setPedidos(data)
      }
    } catch (e) {
      console.error(e)
      setLoadError("Não foi possível carregar os dados. Tente novamente.")
    }
  }, [apiFetch, apiBase, filterStatus])

  const fetchClientes = useCallback(async () => {
    try {
      const res = await apiFetch(`${apiBase}/api/clientes/`)
      if (res.ok) {
        setClientes((await res.json()).filter((c: Cliente) => c.status === "ATIVO"))
      }
    } catch (e) {
      console.error(e)
      setLoadError("Não foi possível carregar os dados. Tente novamente.")
    }
  }, [apiFetch, apiBase])

  const fetchItensVendaveis = useCallback(async () => {
    try {
      const res = await apiFetch(`${apiBase}/api/estoque/itens?vendavel=true`)
      if (res.ok) {
        setItensVendaveis(await res.json())
      }
    } catch (e) {
      console.error(e)
    }
  }, [apiFetch, apiBase])

  const retryLoad = () => {
    setLoadError(null)
    void fetchPedidos()
    void fetchClientes()
    void fetchItensVendaveis()
  }

  useEffect(() => {
    if (!user) return
    if (isSuperAdmin && !selectedTenantId) return
    void fetchPedidos()
    void fetchClientes()
    void fetchItensVendaveis()
  }, [user, isSuperAdmin, selectedTenantId, fetchPedidos, fetchClientes, fetchItensVendaveis])

  useEffect(() => {
    if (user && (!isSuperAdmin || selectedTenantId)) {
      void fetchPedidos()
    }
  }, [user, isSuperAdmin, selectedTenantId, filterStatus, fetchPedidos])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.itens.some(i => i.produto && i.quantidade > 0)) {
      setSubmitError("Adicione pelo menos um item com produto e quantidade.")
      return
    }
    setSubmitError(null)
    const payload = {
      cliente_id: form.cliente_id || null,
      data_pedido: form.data_pedido,
      observacao: form.observacao.trim() || null,
      itens: form.itens
        .filter(i => i.produto && i.quantidade > 0)
        .map(i => ({
          produto: i.produto,
          quantidade: Number(i.quantidade),
          unidade: i.unidade,
          preco_unitario: Number(i.preco_unitario) || 0,
          item_estoque_id: i.item_estoque_id ?? null,
        })),
    }
    try {
      const url = editPedido 
        ? `${apiBase}/api/pedidos/${editPedido.id}`
        : `${apiBase}/api/pedidos/`
      const res = await apiFetch(url, {
        method: editPedido ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      if (res.ok) {
        setShowForm(false)
        setEditPedido(null)
        setForm({
          cliente_id: "",
          data_pedido: todayISODate(),
          observacao: "",
          itens: [defaultFormItem()],
        })
        fetchPedidos()
      } else {
        const errData = await res.json().catch(() => ({})) as { detail?: string }
        setSubmitError(typeof errData?.detail === "string" ? errData.detail : "Erro ao salvar.")
      }
    } catch (e) {
      console.error(e)
      setSubmitError("Erro de conexao.")
    }
  }

  const handleConfirmar = async (pedido: Pedido) => {
    setPedidoToConfirm(pedido)
    setValorEntrada("")
    resetParcelasForm()
    setGerarConta(true)
    setShowConfirmModal(true)
  }

  const handleConfirmarPedido = async () => {
    if (!pedidoToConfirm) return
    
    try {
      const res = await apiFetch(`${apiBase}/api/pedidos/${pedidoToConfirm.id}/confirmar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          gerar_conta: gerarConta,
          valor_entrada: valorEntradaNum || null,
          parcelas: gerarConta ? parcelas.map(p => ({
            valor: parseBRL(p.valor),
            prazo: p.prazo,
            tipo_prazo: p.tipoPrazo,
            data_base: p.dataBase || null
          })) : []
        }),
      })
      if (res.ok) {
        setShowConfirmModal(false)
        setPedidoToConfirm(null)
        fetchPedidos()
      } else {
        const errData = await res.json().catch(() => ({})) as { detail?: string }
        alert(errData?.detail || "Erro ao confirmar.")
      }
    } catch (e) {
      console.error(e)
      alert("Erro de conexao.")
    }
  }

  const handleEntregar = async (pedido: Pedido) => {
    if (!confirm(`Marcar pedido ${pedido.numero} como entregue? Isso dara baixa no estoque.`)) return
    try {
      const res = await apiFetch(`${apiBase}/api/pedidos/${pedido.id}/entregar`, {
        method: "POST",
      })
      if (res.ok) {
        fetchPedidos()
      } else {
        const errData = await res.json().catch(() => ({})) as { detail?: string }
        alert(errData?.detail || "Erro ao entregar.")
      }
    } catch (e) {
      console.error(e)
      alert("Erro de conexao.")
    }
  }

  const handleCancelar = async (pedido: Pedido) => {
    if (!confirm(`Cancelar pedido ${pedido.numero}?`)) return
    try {
      const res = await apiFetch(`${apiBase}/api/pedidos/${pedido.id}/cancelar`, {
        method: "POST",
      })
      if (res.ok) {
        fetchPedidos()
      } else {
        const errData = await res.json().catch(() => ({})) as { detail?: string }
        alert(errData?.detail || "Erro ao cancelar.")
      }
    } catch (e) {
      console.error(e)
      alert("Erro de conexao.")
    }
  }

  const addItem = () => {
    setForm({
      ...form,
      itens: [...form.itens, defaultFormItem()],
    })
  }

  const removeItem = (index: number) => {
    if (form.itens.length > 1) {
      setForm({
        ...form,
        itens: form.itens.filter((_, i) => i !== index),
      })
    }
  }

  const updateItem = (index: number, field: keyof FormPedidoItem, value: string | number | boolean | null) => {
    const newItens = [...form.itens]
    newItens[index] = { ...newItens[index], [field]: value }
    setForm({ ...form, itens: newItens })
  }

  const handleProdutoSelect = (index: number, value: string) => {
    const newItens = [...form.itens]
    const current = newItens[index]
    if (value === "__manual__") {
      newItens[index] = {
        ...current,
        produtoManual: true,
        item_estoque_id: null,
        produto: current.produtoManual ? current.produto : "",
      }
    } else if (!value) {
      newItens[index] = {
        ...current,
        produtoManual: false,
        item_estoque_id: null,
        produto: "",
      }
    } else {
      const catalogo = itensVendaveis.find((i) => i.id === value)
      if (!catalogo) return
      const unidade = normalizeUnidadeProduto(catalogo.unidade)
      newItens[index] = {
        ...current,
        produtoManual: false,
        item_estoque_id: catalogo.id,
        produto: catalogo.nome,
        unidade,
        preco_unitario: catalogo.preco_medio > 0 ? catalogo.preco_medio : current.preco_unitario,
      }
    }
    setForm({ ...form, itens: newItens })
  }

  const calcularTotal = () => {
    return form.itens.reduce((acc, item) => {
      return acc + (Number(item.quantidade) || 0) * (Number(item.preco_unitario) || 0)
    }, 0)
  }

  if (isSuperAdmin && !selectedTenantId) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Selecione uma granja no menu lateral.</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Pedidos de Venda</h1>
        <Button onClick={() => {
          setSubmitError(null)
          setShowForm(true)
          setEditPedido(null)
          setForm({
            cliente_id: "",
            data_pedido: todayISODate(),
            observacao: "",
            itens: [defaultFormItem()],
          })
        }}>
          <Plus className="h-4 w-4 mr-2" /> Novo Pedido
        </Button>
      </div>

      {loadError && (
        <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm flex items-center justify-between gap-2">
          <span>{loadError}</span>
          <Button type="button" variant="outline" size="sm" onClick={retryLoad}>
            Tentar novamente
          </Button>
        </div>
      )}

      <div className="flex gap-2">
        <select
          className="text-sm border rounded px-2 py-1 bg-background"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">Todos os status</option>
          <option value="RASCUNHO">Rascunho</option>
          <option value="CONFIRMADO">Confirmado</option>
          <option value="ENTREGUE">Entregue</option>
          <option value="CANCELADO">Cancelado</option>
        </select>
      </div>

      <Dialog
        open={showForm || !!editPedido}
        onOpenChange={(open) => {
          if (!open) {
            setShowForm(false)
            setEditPedido(null)
            setSubmitError(null)
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto gap-0 p-0">
          <DialogTitle className="sr-only">
            {editPedido ? `Editar Pedido ${editPedido.numero}` : 'Novo Pedido'}
          </DialogTitle>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold pr-10">
              {editPedido ? `Editar Pedido ${editPedido.numero}` : 'Novo Pedido'}
            </h2>
            {submitError && (
              <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm">
                {submitError}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Cliente</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2"
                    value={form.cliente_id}
                    onChange={(e) => setForm({ ...form, cliente_id: e.target.value })}
                  >
                    <option value="">Selecione...</option>
                    {clientes.map(c => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Data do Pedido</Label>
                  <Input
                    type="date"
                    value={form.data_pedido}
                    onChange={(e) => setForm({ ...form, data_pedido: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label>Itens</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addItem}>
                    <Plus className="h-4 w-4 mr-1" /> Adicionar Item
                  </Button>
                </div>
                <div className="space-y-2">
                  {form.itens.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-4 space-y-1">
                        <Label className="sr-only">Produto</Label>
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={
                            item.produtoManual
                              ? "__manual__"
                              : item.item_estoque_id || ""
                          }
                          onChange={(e) => handleProdutoSelect(index, e.target.value)}
                        >
                          <option value="">Selecione do catalogo...</option>
                          {item.item_estoque_id &&
                            !item.produtoManual &&
                            !itensVendaveis.some((i) => i.id === item.item_estoque_id) && (
                              <option value={item.item_estoque_id}>{item.produto}</option>
                            )}
                          {itensVendaveis.map((iv) => (
                            <option key={iv.id} value={iv.id}>
                              {iv.nome} ({iv.unidade})
                            </option>
                          ))}
                          <option value="__manual__">Outro (digitar manualmente)</option>
                        </select>
                        {item.produtoManual && (
                          <Input
                            placeholder="Nome do produto"
                            value={item.produto}
                            onChange={(e) => updateItem(index, "produto", e.target.value)}
                          />
                        )}
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          placeholder="Qtd"
                          min={0}
                          value={item.quantidade || ""}
                          onChange={(e) => updateItem(index, "quantidade", parseNonNegative(e.target.value))}
                        />
                      </div>
                      <div className="col-span-2">
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={item.unidade}
                          onChange={(e) => updateItem(index, "unidade", e.target.value)}
                        >
                          {UNIDADES_PRODUTO.map((u) => (
                            <option key={u} value={u}>
                              {u === "DZ" ? "Duzia" : u === "UN" ? "Unidade" : "Caixa"}
                            </option>
                          ))}
                          {!UNIDADES_PRODUTO.includes(item.unidade as (typeof UNIDADES_PRODUTO)[number]) && (
                            <option value={item.unidade}>{item.unidade}</option>
                          )}
                        </select>
                      </div>
                      <div className="col-span-3">
                        <Input
                          type="number"
                          placeholder="Preco unit."
                          min={0}
                          step="0.01"
                          value={item.preco_unitario || ""}
                          onChange={(e) => updateItem(index, "preco_unitario", parseNonNegative(e.target.value))}
                        />
                      </div>
                      <div className="col-span-1">
                        <Button type="button" variant="ghost" size="icon" aria-label={`Remover item ${index + 1}`} onClick={() => removeItem(index)} disabled={form.itens.length === 1}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-right text-lg font-bold">
                  Total: {formatBRL(calcularTotal())}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Observacao</Label>
                <textarea
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.observacao}
                  onChange={(e) => setForm({ ...form, observacao: e.target.value })}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit">Salvar Rascunho</Button>
                <Button variant="outline" type="button" onClick={() => { setShowForm(false); setEditPedido(null); setSubmitError(null) }}>
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid gap-4">
        {pedidos.map((p) => (
          <Card key={p.id}>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-mono font-bold">{p.numero}</span>
                    <Badge variant={STATUS_COLORS[p.status] || "secondary"}>{STATUS_LABELS[p.status] || p.status}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Cliente: {p.cliente_nome || "Nao informado"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Data: {formatBRDate(p.data_pedido)}
                  </p>
                  {p.observacao && (
                    <p className="text-sm text-muted-foreground mt-1">Obs: {p.observacao}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold">{formatBRL(p.valor_total)}</p>
                  <p className="text-xs text-muted-foreground">
                    {p.itens.length} {p.itens.length === 1 ? "item" : "itens"}
                  </p>
                </div>
              </div>
              
              {p.itens.length > 0 && (
                <div className="mt-4 border-t pt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-1">Produto</th>
                        <th className="text-right py-1">Qtd</th>
                        <th className="text-right py-1">Unid.</th>
                        <th className="text-right py-1">Preco</th>
                        <th className="text-right py-1">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.itens.map((item, idx) => (
                        <tr key={idx} className="border-b">
                          <td className="py-1">{item.produto}</td>
                          <td className="text-right py-1">{item.quantidade}</td>
                          <td className="text-right py-1">{item.unidade}</td>
                          <td className="text-right py-1">{formatBRL(item.preco_unitario)}</td>
                          <td className="text-right py-1">{formatBRL(item.subtotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-4 flex gap-2">
                {p.status === "RASCUNHO" && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditPedido(p)
                        setForm({
                          cliente_id: p.cliente_id || "",
                          data_pedido: p.data_pedido,
                          observacao: p.observacao || "",
                          itens: p.itens.map(i => ({
                            produto: i.produto,
                            quantidade: i.quantidade,
                            unidade: i.unidade,
                            preco_unitario: i.preco_unitario,
                            item_estoque_id: i.item_estoque_id ?? null,
                            produtoManual: !i.item_estoque_id,
                          })),
                        })
                      }}
                    >
                      <Pencil className="h-4 w-4 mr-1" /> Editar
                    </Button>
                    <Button size="sm" onClick={() => handleConfirmar(p)}>
                      Confirmar
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => handleCancelar(p)}>
                      Cancelar
                    </Button>
                  </>
                )}
                {p.status === "CONFIRMADO" && (
                  <>
                    <Button size="sm" onClick={() => handleEntregar(p)}>
                      Marcar Entregue
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => handleCancelar(p)}>
                      Cancelar
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {pedidos.length === 0 && (
        <div className="py-8 text-center text-muted-foreground">
          <p>Nenhum pedido encontrado. Crie um pedido para iniciar uma venda.</p>
          <Button variant="outline" className="mt-3" onClick={() => { setSubmitError(null); setShowForm(true); setEditPedido(null); setForm({ cliente_id: "", data_pedido: todayISODate(), observacao: "", itens: [defaultFormItem()] }) }}>Novo Pedido</Button>
        </div>
      )}

      <Dialog open={showConfirmModal && !!pedidoToConfirm} onOpenChange={(open) => { if (!open) { setShowConfirmModal(false); setPedidoToConfirm(null) } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] w-[calc(100vw-2rem)] gap-0 overflow-hidden p-0 sm:w-full">
          <DialogTitle className="sr-only">Confirmar pedido</DialogTitle>
          {pedidoToConfirm && (
          <div className="flex max-h-[90vh] min-h-0 flex-col">
            <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-x-contain">
          <Card className="border-0 shadow-none bg-white dark:bg-slate-900">
            <CardHeader className="bg-white dark:bg-slate-900">
              <CardTitle className="pr-10">Confirmar Pedido {pedidoToConfirm.numero}</CardTitle>
              <p className="text-sm text-muted-foreground">
                Total: {formatBRL(pedidoToConfirm.valor_total)}
              </p>
            </CardHeader>
            <CardContent className="space-y-4 bg-white pb-4 dark:bg-slate-900">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={gerarConta}
                onChange={e => setGerarConta(e.target.checked)}
                className="rounded"
              />
              Gerar conta(s) a receber
            </label>

            {gerarConta && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Entrada (recebido agora)</Label>
                    <Input
                      placeholder="R$ 0,00"
                      value={valorEntrada}
                      onChange={e => setValorEntrada(maskMoneyInput(e.target.value))}
                    />
                  </div>
                  <div>
                    <Label>Saldo a receber</Label>
                    <Input
                      value={formatBRL(saldoDevedor)}
                      disabled
                      className="bg-muted"
                    />
                  </div>
                </div>

                {saldoDevedor > 0 && (
                  <>
                    <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg text-xs text-blue-700 dark:text-blue-300">
                      <strong>Como funciona:</strong> Marque "Restante" para calcular automaticamente.
                      Use "Parcelar em" para dividir em varias parcelas.
                    </div>

                    <div className="min-w-0 max-w-full overflow-x-auto">
                    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 2xl:grid-cols-7 min-w-0 sm:min-w-[36rem] 2xl:min-w-0">
                      <div className="flex items-center gap-2 pt-6">
                        <input
                          type="checkbox"
                          checked={novaParcela.ehRestante}
                          onChange={e => setNovaParcela({ ...novaParcela, ehRestante: e.target.checked })}
                          className="rounded"
                        />
                        <Label className="cursor-pointer">Restante</Label>
                      </div>
                      <div>
                        <Label>Valor</Label>
                        <Input
                          placeholder="0,00"
                          value={novaParcela.ehRestante ? formatBRL(restanteParaPagar) : novaParcela.valor}
                          onChange={e => setNovaParcela({ ...novaParcela, valor: maskMoneyInput(e.target.value) })}
                          disabled={novaParcela.ehRestante}
                        />
                      </div>
                      <div>
                        <Label>Parcelar em</Label>
                        <Input
                          type="number"
                          min="1"
                          max="48"
                          value={novaParcela.parcelarEm || ""}
                          onChange={e => setNovaParcela({ ...novaParcela, parcelarEm: parseInt(e.target.value) || 1 })}
                        />
                      </div>
                      <div>
                        <Label>Intervalo</Label>
                        <Input
                          type="number"
                          min="1"
                          value={novaParcela.prazo || ""}
                          onChange={e => setNovaParcela({ ...novaParcela, prazo: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                      <div>
                        <Label>Tipo</Label>
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2"
                          value={novaParcela.tipoPrazo}
                          onChange={e => setNovaParcela({ ...novaParcela, tipoPrazo: e.target.value as "dias" | "semanas" | "meses" })}
                        >
                          <option value="dias">Dias</option>
                          <option value="semanas">Semanas</option>
                          <option value="meses">Meses</option>
                        </select>
                      </div>
                      <div className="min-w-[9.5rem]">
                        <Label>Data Base</Label>
                        <Input
                          type="date"
                          className="w-full min-w-[9.5rem]"
                          value={novaParcela.dataBase}
                          onChange={e => setNovaParcela({ ...novaParcela, dataBase: e.target.value })}
                        />
                      </div>
                      <div className="flex items-end">
                        <Button type="button" variant="outline" onClick={adicionarParcela} className="w-full">
                          <Plus className="h-4 w-4 mr-1" /> Add
                        </Button>
                      </div>
                    </div>
                    </div>

                    {novaParcela.ehRestante && novaParcela.parcelarEm > 0 && novaParcela.prazo > 0 && (
                      <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg text-sm text-green-800 dark:text-green-200">
                        {(() => {
                          const valores = splitInstallments(restanteParaPagar, novaParcela.parcelarEm)
                          const n = novaParcela.parcelarEm
                          if (n === 1) {
                            return <><strong>Sera criada 1 parcela de {formatBRL(valores[0])}</strong><br />com intervalo de {novaParcela.prazo} {novaParcela.tipoPrazo}</>
                          }
                          const iguais = valores.every(v => v === valores[0])
                          return iguais ? (
                            <><strong>Serao criadas {n} parcelas de {formatBRL(valores[0])}</strong><br />com intervalo de {novaParcela.prazo} {novaParcela.tipoPrazo}</>
                          ) : (
                            <><strong>Serao criadas {n} parcelas de {formatBRL(valores[0])} (ultima {formatBRL(valores[n - 1])})</strong><br />com intervalo de {novaParcela.prazo} {novaParcela.tipoPrazo}. O centavo restante vai para a ultima parcela.</>
                          )
                        })()}
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="ghost" size="sm" onClick={() => adicionarParcelaRapidaPorTipo(1, 30, "dias")}>
                        1x em 30 dias
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => adicionarParcelaRapidaPorTipo(2, 1, "meses")}>
                        2x mensal
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => adicionarParcelaRapidaPorTipo(3, 1, "meses")}>
                        3x mensal
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => adicionarParcelaRapidaPorTipo(4, 1, "meses")}>
                        4x mensal
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => adicionarParcelaRapidaPorTipo(12, 1, "meses")}>
                        12x mensal
                      </Button>
                    </div>

                    {parcelas.length > 0 && (
                      <div className="space-y-2">
                        {parcelas.map((p, i) => (
                          <div key={i} className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                            <div>
                              <span className="font-medium">Parcela {i + 1}</span>
                              <span className="text-muted-foreground ml-2">
                                em {p.prazo} {p.tipoPrazo}
                                {p.dataBase && <span className="text-xs">(a partir de {formatBRDate(p.dataBase)})</span>}
                              </span>
                              <span className="text-xs text-muted-foreground ml-2">
                                = {formatBRDate(calcularDataParcela(p.prazo, p.tipoPrazo, p.dataBase))}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="font-medium">{formatBRL(parseBRL(p.valor))}</span>
                              <Button type="button" variant="ghost" size="sm" onClick={() => removerParcela(i)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            )}

          </CardContent>
          </Card>
            </div>
            <div className="sticky bottom-0 z-10 flex shrink-0 flex-col gap-2 border-t bg-background p-4 sm:flex-row">
              <Button className="w-full sm:w-auto" onClick={handleConfirmarPedido}>
                Confirmar Pedido
              </Button>
              <Button className="w-full sm:w-auto" variant="outline" onClick={() => { setShowConfirmModal(false); setPedidoToConfirm(null) }}>
                Cancelar
              </Button>
            </div>
          </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/pedidos')({
  component: PedidosPage,
})
